/**
 * Cloudflare D1 Rest API Service
 * 
 * Secure client for Espacero D1 Worker.
 * Replaces the old SQL-proxy client.
 */


// import { getAuthToken } from './auth'; // Removed

import { getWorkerUrl } from '../utils/env';
import type { DemandAssistResponse, DemandPlatformStats, VenuePastEvent, RecentVenueEvent } from '../types';

const WORKER_URL = getWorkerUrl('D1');
let didWarnMissingWorkerUrl = false;

const REQUEST_TIMEOUT_MS = 12000;
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

export function isD1Configured(): boolean {
  return !!WORKER_URL;
}

function warnMissingWorkerUrlOnce() {
  if (didWarnMissingWorkerUrl) return;
  didWarnMissingWorkerUrl = true;
  console.warn(
    'Cloudflare Worker URL nie je nastavený. Aplikácia používa localStorage fallback. ' +
    'Nastavte VITE_CLOUDFLARE_WORKER_URL v .env (.env.local) ak chcete používať D1.'
  );
}

async function request(path: string, method: string, body?: any, params?: Record<string, any>) {
  if (!WORKER_URL) {
    warnMissingWorkerUrlOnce();
    throw new Error('Cloudflare Worker URL nie je nastavený');
  }

  let url = `${WORKER_URL}${path}`;
  if (params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) search.append(k, String(v));
    });
    const qs = search.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // Prioritize Clerk token if available
  let clerkToken = null;
  const clerk = typeof window !== 'undefined' ? (window as any).Clerk : null;
  if (clerk?.session) {
    try {
      clerkToken = await clerk.session.getToken();
    } catch (e) {
      console.error('Failed to get Clerk token:', e);
    }
  }

  if (clerkToken) {
    headers['Authorization'] = `Bearer ${clerkToken}`;
  }


  // Phase 2: Worker sessions are now handled via HttpOnly Cookies (espacero_session).
  // These are sent automatically due to credentials: 'include' below.
  
  const opts: RequestInit = {
    method,
    headers,
    credentials: 'include', // CRITICAL: Required for same-site cookies (espacero_session / Cloudflare Access)
  };

  // Cloudflare Access handles authentication via cookies (fallback)

  if (body) {
    if (method === 'GET') {
      // Body not allowed in GET usually, but if needed we can handle it. 
      // Here we assume params handle GET data.
    } else {
      opts.body = JSON.stringify(body);
    }
  }


  const res = await fetchWithTimeoutAndSingleRetry(url, opts, REQUEST_TIMEOUT_MS);

  if (!res.ok) {
    if (res.status === 404) {
      try {
        const errorText = await res.text();
        console.warn(`[D1] 404 Not Found for ${url}. Response:`, errorText);
      } catch (e) {
        console.warn(`[D1] 404 Not Found for ${url}. Could not read body.`, e);
      }
      return null;
    }

    let errorMsg = res.statusText;
    try {
      const errData = await res.json();
      errorMsg = errData.error || errData.message || errorMsg;
      if (errData.details) errorMsg += ` (${errData.details})`;
    } catch { }

    if (res.status === 401) throw new Error(errorMsg || 'Unauthorized user');
    if (res.status === 403) throw new Error(errorMsg || 'Forbidden');
    throw new Error(`API Error ${res.status}: ${errorMsg}`);
  }

  const data = await res.json();
  if (data.error) throw new Error(data.error);

  return data;
}

async function fetchWithTimeoutAndSingleRetry(url: string, opts: RequestInit, timeoutMs: number): Promise<Response> {
  const first = await fetchWithTimeout(url, opts, timeoutMs);
  // Only retry safe idempotent GET requests to avoid duplicate non-idempotent writes (Finding 10)
  const isIdempotent = (opts.method || 'GET').toUpperCase() === 'GET';
  if (!isIdempotent || !RETRYABLE_STATUSES.has(first.status)) return first;

  // Short jittered backoff to smooth over transient worker/network errors.
  await new Promise((resolve) => setTimeout(resolve, 250 + Math.floor(Math.random() * 250)));
  return fetchWithTimeout(url, opts, timeoutMs);
}

async function fetchWithTimeout(url: string, opts: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs} ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

const get = (path: string, params?: any) => request(path, 'GET', undefined, params);
const post = (path: string, body?: any) => request(path, 'POST', body);
const put = (path: string, body?: any) => request(path, 'PUT', body);
const patch = (path: string, body?: any) => request(path, 'PATCH', body);
const del = (path: string, body?: any) => request(path, 'DELETE', body);

export const api = {
  setup: async () => post('/api/setup'),

  listings: {
    list: async (params?: any) => {
      const res = await get('/api/listings', params);
      return res?.results || [];
    },
    listWithMeta: async (params?: any) => {
      return await get('/api/listings', params);
    },
    count: async (): Promise<number> => {
      const res = await get('/api/listings/count');
      return res?.count ?? 0;
    },
    get: async (id: string) => {
      const res = await get(`/api/listings/${id}`);
      return res?.results?.[0] || null;
    },
    upsert: async (data: any) => post('/api/listings', data),
    claimByToken: async (id: string, token: string, email: string) =>
      post(`/api/listings/${id}/claim-by-token`, { token, email }),
    remove: async (id: string) => del(`/api/listings/${id}`),
    removeBatch: async (ids: string[]) => del('/api/listings/batch', { ids }),
    updateStatus: async (id: string, status: string) => patch(`/api/listings/${id}/status`, { status }),
    // Admin-only provenance/trust columns (see migrations/20260715_add_trust_columns.sql)
    updateTrust: async (id: string, fields: Record<string, string | boolean | null>) => patch(`/api/listings/${id}`, fields),
    updateImages: async (id: string, images: string[]) => patch(`/api/listings/${id}/images`, { images }),
    updateProfileRequirement: async (id: string, required: boolean) => patch(`/api/listings/${id}/profile-requirement`, { required }),
    trackEvent: async (id: string) => post(`/api/listings/${id}/track`),
    getAnalytics: async (id: string) => {
      const res = await get(`/api/listings/${id}/analytics`);
      return res?.results || [];
    },
    getAnalyticsBulk: async (ids: string[]) => {
      const res = await get('/api/listings/analytics', { ids: ids.join(',') });
      return res?.results || [];
    }
  },

  vendors: {
    list: async (params?: any) => {
      const res = await get('/api/vendors', params);
      return res?.results || [];
    },
    get: async (id: string) => {
      const res = await get(`/api/vendors/${id}`);
      return res?.results?.[0] || null;
    },
    upsert: async (data: any) => post('/api/vendors', data),
    remove: async (id: string) => del(`/api/vendors/${id}`)
  },



  reviews: {
    list: async (listingId: string) => {
      const res = await get('/api/reviews', { listing_id: listingId });
      return res?.results || [];
    },
    getMy: async (listingId: string, userId: string) => {
      const res = await get('/api/reviews', { listing_id: listingId, user_id: userId });
      return res?.results?.[0] || null;
    },
    create: async (data: any) => post('/api/reviews', data)
  },

  favorites: {
    listIds: async (userId: string) => {
      const res = await get('/api/favorites', { user_id: userId });
      return (res?.results || []).map((r: any) => r.listing_id);
    },
    add: async (userId: string, listingId: string) => post('/api/favorites', { user_id: userId, listing_id: listingId }),
    remove: async (userId: string, listingId: string) => del('/api/favorites', { user_id: userId, listing_id: listingId }),
    listDetails: async (ids: string[]) => {
      const res = await post('/api/favorites/listings', { listing_ids: ids });
      return res?.results || [];
    }
  },

  settings: {
    get: async (key: string) => {
      const res = await get(`/api/settings/${key}`);
      return res?.result?.value || null;
    },
    set: async (key: string, value: any) => post('/api/settings', { key, value })
  },
  admin: {
    listUsers: async () => {
      const res = await get('/api/admin/users');
      return res?.results || [];
    },
    getUserListings: async (userId: string) => {
      const res = await get(`/api/admin/users/${userId}/listings`);
      return res?.results || [];
    },
    deleteUser: async (userId: string) => del(`/api/admin/users/${userId}`),
    demandMetrics: async (days = 30) => get('/api/admin/demands/metrics', { days }),
    citationMetrics: async (days = 30) => get('/api/admin/citations/metrics', { days }),
    emailLog: async (limit = 200) => get('/api/admin/emails', { limit }),
    setEmailsEnabled: async (enabled: boolean) => post('/api/admin/emails/toggle', { enabled }),
    social: {
      getOverview: async () => get('/api/admin/social/overview'),
      getPosts: async (params?: Record<string, any>) => get('/api/admin/social/posts', params),
      getPost: async (id: string) => get(`/api/admin/social/posts/${id}`),
      generateDrafts: async (count = 4) => post('/api/admin/social/generate-drafts', { count }),
      createPost: async (data: any) => post('/api/admin/social/posts', data),
      updatePost: async (id: string, data: any) => patch(`/api/admin/social/posts/${id}`, data),
      approvePost: async (id: string) => post(`/api/admin/social/posts/${id}/approve`, {}),
      rejectPost: async (id: string) => post(`/api/admin/social/posts/${id}/reject`, {}),
      schedulePost: async (id: string, scheduledFor: string) => post(`/api/admin/social/posts/${id}/schedule`, { scheduled_for: scheduledFor }),
      unschedulePost: async (id: string) => post(`/api/admin/social/posts/${id}/unschedule`, {}),
      publishPost: async (id: string) => post(`/api/admin/social/posts/${id}/publish`, {}),
      verifyStatus: async (id: string) => post(`/api/admin/social/posts/${id}/verify-status`, {}),
      getSettings: async () => get('/api/admin/social/settings'),
      updateSettings: async (settings: any) => post('/api/admin/social/settings', settings),
      testConnection: async () => post('/api/admin/social/test-connection', {}),
      runScheduler: async () => post('/api/admin/social/run-scheduler', {})
    }
  },
  auth: {
    sync: async (data: { email?: string; name?: string; role?: string | null }) =>
      post('/api/auth/sync', data) as Promise<{ success: boolean; role?: string | null } | null>
  },
  reports: {
    create: async (data: { listingId: string; listingTitle: string; reason: string; details: string; email?: string }) => post('/api/reports', data)
  },
  demands: {
    list: async (filters: any = {}) => {
      const res = await get('/api/demands', filters);
      return res?.demands || [];
    },
    stats: async (): Promise<DemandPlatformStats> => {
      const res = await get('/api/demands/stats');
      return res?.stats || { total: 0, answered: 0, totalResponses: 0, responseRate: 0, cities: [] };
    },
    my: async () => {
      const res = await get('/api/demands/my');
      return res?.demands || [];
    },
    get: async (id: string, access?: string) => {
      const q = access ? `?access=${encodeURIComponent(access)}` : '';
      return get(`/api/demands/${id}${q}`);
    },
    getPublic: async (id: string) => {
      // Public endpoint — no auth token. Returns sanitized demand without contacts.
      const base = WORKER_URL || (import.meta.env.VITE_API_URL as string) || 'https://api.espacero.com';
      const res = await fetch(`${base}/api/demands/${id}/public`);
      if (!res.ok) throw new Error('Dopyt nenájdený');
      return res.json() as Promise<{ demand: any }>;
    },
    create: async (data: any) => post('/api/demands', data),
    assist: async (data: any): Promise<DemandAssistResponse> => post('/api/demands/assist', data),
    remove: async (id: string) => del(`/api/demands/${id}`),
    respond: async (demandId: string, data: { listing_id: string; message: string }) =>
      post(`/api/demands/${demandId}/respond`, data),
    getResponses: async (demandId: string, access?: string) => {
      const q = access ? `?access=${encodeURIComponent(access)}` : '';
      const res = await get(`/api/demands/${demandId}/responses${q}`);
      return res?.responses || [];
    },
  },
  ownerPrefs: {
    get: async () => {
      const res = await get('/api/owner/demand-prefs');
      return res?.prefs || null;
    },
    save: async (data: any) => put('/api/owner/demand-prefs', data),
  },
  pastEvents: {
    recent: async (limit?: number, options?: { fresh?: boolean }): Promise<RecentVenueEvent[]> => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (options?.fresh) params.set('fresh', '1');
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await get(`/api/venue-past-events/recent${query}`);
      return res?.events || [];
    },
    getByVenue: async (venueId: string, all = false): Promise<VenuePastEvent[]> => {
      const res = await get(`/api/listings/${venueId}/past-events`, all ? { all: 1 } : undefined);
      return res?.events || [];
    },
    updateStatus: async (venueId: string, eventId: string, statusOrAction: string) => {
      return patch(`/api/listings/${venueId}/past-events/${eventId}`, { action: statusOrAction });
    },
  }
};

export const d1 = api;


